
// Import the necessary components and hooks
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchLessonById } from '@/services/modules/lessonService';
import { ModuleLesson, TaxonomyLevel } from '@/types/supabase';
import { LessonContentRenderer } from '@/components/learning/LessonContentRenderer';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Info } from 'lucide-react';
import TaxonomyBadge from '@/components/learning/TaxonomyBadge';
import TaxonomyReflection from '@/components/learning/TaxonomyReflection';
import { updateLessonCompletionStatus, updateUserProgress, calculateModuleCompletion } from '@/services/modules/lessonService';
import { useToast } from '@/hooks/use-toast';

const LessonDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [lesson, setLesson] = useState<ModuleLesson | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const fetchLesson = async () => {
      if (!id) return;
      
      setIsLoading(true);
      try {
        const lessonData = await fetchLessonById(id);
        if (lessonData) {
          setLesson(lessonData);
          setIsCompleted(lessonData.is_completed || false);
        }
      } catch (error) {
        console.error('Error fetching lesson:', error);
        toast({
          title: "Error",
          description: "Failed to load the lesson. Please try again.",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchLesson();
  }, [id, toast]);

  const handleCompletionUpdate = async (completed: boolean) => {
    if (!lesson) return;
    
    setIsCompleted(completed);
    
    // Update module progress after lesson completion status changes
    try {
      const completionPercentage = await calculateModuleCompletion(lesson.module_id);
      await updateUserProgress(lesson.module_id, completionPercentage);
    } catch (error) {
      console.error('Error updating module progress:', error);
    }
  };

  const handleBack = () => {
    if (lesson?.module_id) {
      navigate(`/modules/${lesson.module_id}`);
    } else {
      navigate('/modules');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold">Lesson not found</h2>
        <p className="mt-2 text-muted-foreground">The lesson you are looking for does not exist or has been removed.</p>
        <Button onClick={() => navigate('/modules')} className="mt-4">
          Return to Modules
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 max-w-4xl">
      <div className="mb-6">
        <Button variant="ghost" onClick={handleBack} className="mb-2">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Module
        </Button>
        
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">{lesson.title}</h1>
          {lesson.taxonomy_level && (
            <TaxonomyBadge 
              taxonomyLevel={lesson.taxonomy_level as TaxonomyLevel} 
              className="text-xs" 
            />
          )}
        </div>
        
        {lesson.description && (
          <p className="mt-2 text-muted-foreground">{lesson.description}</p>
        )}
        
        <div className="flex items-center gap-2 mt-4">
          {lesson.duration_minutes && (
            <div className="text-sm bg-secondary text-secondary-foreground px-2 py-1 rounded-md">
              {lesson.duration_minutes} minutes
            </div>
          )}
          <div className={`text-sm px-2 py-1 rounded-md ${isCompleted ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'}`}>
            {isCompleted ? 'Completed' : 'Not completed'}
          </div>
        </div>
      </div>

      <div className="bg-card rounded-lg border p-6 mb-8">
        {lesson.content ? (
          <LessonContentRenderer 
            content={lesson.content} 
            lessonId={lesson.id} 
            isCompleted={isCompleted}
            onCompletionUpdate={handleCompletionUpdate}
          />
        ) : (
          <div className="text-center py-8">
            <Info className="mx-auto h-12 w-12 text-muted-foreground mb-2" />
            <p>No content available for this lesson.</p>
          </div>
        )}
      </div>

      {lesson.taxonomy_level && (
        <TaxonomyReflection 
          lessonId={lesson.id} 
          taxonomyLevel={lesson.taxonomy_level}
          moduleId={lesson.module_id}
        />
      )}
    </div>
  );
};

export default LessonDetail;
